import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

import {
  CEIL_Y,
  FIRING_X,
  HALL_MAX_X,
  HALL_MAX_Z,
  HALL_MIN_X,
  HALL_MIN_Z,
  TUNNEL_H,
  TUNNEL_HALF_W,
  type HideoutLayout,
} from "../game/hideout/layout.js";
import { AO_BOTTOM, AO_SIDE, AO_TOP, hashString, rand01, randSym } from "./palette.js";

/**
 * Everything in the bunker that is not a wall. `layout.ts` builds the SHELL;
 * this builds the ROOM: nine stations that grow out of junk piles as they are
 * built, clutter that disappears as modules are cleared (`clearedAt` against
 * `layout.builtCount`), and the lights.
 *
 * Also: four I-beams under the slab, so 28 m of ceiling has a grain; the
 * range's lane markings, sandbags and distance boards; and the paint on each
 * target face, which follows `RangeTarget.kind`.
 *
 * Nothing here collides (the same trade `render/props.ts` makes). Draw calls
 * are the budget: one merged vertex-coloured geometry per station, one for the
 * clutter, one for the structure, one for the target dressing, one for the
 * bulbs, one per target glow, all on one `MeshLambertMaterial`, painted flat
 * with the three-step baked AO from `palette.ts`. Every wobble and tint comes
 * from `rand01`/`randSym` with a fixed per-item seed, never `Math.random`.
 */

// ---------------------------------------------------------------------------
// Palette — the bunker's own colours; nothing outside this file paints with
// them.
// ---------------------------------------------------------------------------

const C_STEEL = 0xa9adb1;
const C_STEEL_DARK = 0x5d6368;
const C_IRON = 0x43484c;
const C_WOOD = 0x7a5a3a;
const C_WOOD_DARK = 0x513c26;
const C_COPPER = 0xb87333;
const C_CANVAS = 0xb9a77d;
const C_TARP = 0x4d5a3e;
const C_RUBBLE = 0x6d7370;
const C_RUBBLE_DARK = 0x565b58;
const C_OXIDE = 0x8c4632;
const C_RED = 0xa8332a;
const C_WHITE = 0xd9d5c9;
const C_PAPER = 0xd2c4a0;
const C_RUST = 0x6f4a2e;
const C_HIVE = 0xcfc6a6;
const C_GLASS = 0x6d8b90;
const C_RUBBER = 0x2e3134;
const C_LAMP_ON = 0xffd2a0;
const C_LAMP_COLD = 0x9fb4d6;
/** The figure painted on a range silhouette. */
const C_SILHOUETTE = 0x2b2f33;

/** Warm working light: the generator is running and the shelter is lit. */
const LAMP_WARM = 0xffd2a0;
const LAMP_WARM_INTENSITY = 38;
const LAMP_WARM_DISTANCE = 14;
/** Battery emergency light: navigable, and unmistakably not "on". */
const LAMP_COLD = 0x9fb4d6;
const LAMP_COLD_INTENSITY = 10;
const LAMP_COLD_DISTANCE = 12;
/** Physically-based falloff, matching the rest of the renderer's lights. */
const LAMP_DECAY = 2;
/** Bulbs hang just under the hall slab; the tunnel's under its own, lower one. */
const LAMP_Y = CEIL_Y - 0.2;
const TUNNEL_LAMP_Y = TUNNEL_H - 0.2;
/** Downrange x of the four tunnel bulbs — also where their shades go. */
const TUNNEL_LAMP_X: readonly number[] = [22, 31, 40, 49];

/** Additive shell over a plate, flipped visible for the drill. */
const GLOW_COLOR = 0xffc860;
const GLOW_OPACITY = 0.55;
/** How much bigger than the plate, so the glow reads as a halo and not a skin. */
const GLOW_SCALE = 1.06;

// ---------------------------------------------------------------------------
// Public shape
// ---------------------------------------------------------------------------

export interface BuiltHideoutProps {
  group: THREE.Group;
  triangles: number;
  /**
   * One invisible additive shell per range target, keyed by `RangeTarget.id`.
   * Lighting a plate is a gameplay event, so the range controller flips
   * `.visible`.
   */
  targetGlows: ReadonlyMap<string, THREE.Object3D>;
  dispose(): void;
}

export interface HideoutPropsOptions {
  /** Warm light and a running engine, or emergency lamps and a dead one. */
  generatorRunning: boolean;
}

// ---------------------------------------------------------------------------
// The box kit: four calls that append a painted geometry to a `Parts` array,
// merged exactly once — a twenty-box workbench is one draw call.
// ---------------------------------------------------------------------------

type Parts = THREE.BufferGeometry[];

/**
 * Paints a geometry one colour with baked AO from the vertex NORMAL — the same
 * three constants `mapMesh.ts` bakes into the walls.
 */
function paint(geo: THREE.BufferGeometry, hex: number): THREE.BufferGeometry {
  const pos = geo.attributes["position"] as THREE.BufferAttribute;
  const nrm = geo.attributes["normal"] as THREE.BufferAttribute | undefined;
  const c = new THREE.Color(hex);
  const colors = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const ny = nrm === undefined ? 0 : nrm.getY(i);
    const ao = ny > 0.5 ? AO_TOP : ny < -0.5 ? AO_BOTTOM : AO_SIDE;
    const o = i * 3;
    colors[o] = c.r * ao;
    colors[o + 1] = c.g * ao;
    colors[o + 2] = c.b * ao;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return geo;
}

/** A painted box, optionally spun about Y, centred at (cx, cy, cz). */
function addBox(
  parts: Parts,
  w: number,
  h: number,
  d: number,
  cx: number,
  cy: number,
  cz: number,
  color: number,
  yaw = 0,
): void {
  const geo = new THREE.BoxGeometry(w, h, d);
  geo.deleteAttribute("uv");
  paint(geo, color);
  if (yaw !== 0) geo.rotateY(yaw);
  geo.translate(cx, cy, cz);
  parts.push(geo);
}

/** ...and one tipped over, for planks, fallen shelves and hanging cable. */
function addTilted(
  parts: Parts,
  w: number,
  h: number,
  d: number,
  cx: number,
  cy: number,
  cz: number,
  color: number,
  rx: number,
  ry: number,
  rz: number,
): void {
  const geo = new THREE.BoxGeometry(w, h, d);
  geo.deleteAttribute("uv");
  paint(geo, color);
  if (rx !== 0) geo.rotateX(rx);
  if (rz !== 0) geo.rotateZ(rz);
  if (ry !== 0) geo.rotateY(ry);
  geo.translate(cx, cy, cz);
  parts.push(geo);
}

/** Drums, stills and bar plates: low-segment so it still reads as facets. */
function addCyl(
  parts: Parts,
  rTop: number,
  rBot: number,
  h: number,
  cx: number,
  cy: number,
  cz: number,
  color: number,
  segs = 10,
): void {
  const geo = new THREE.CylinderGeometry(rTop, rBot, h, segs, 1);
  geo.deleteAttribute("uv");
  paint(geo, color);
  geo.translate(cx, cy + h / 2, cz);
  parts.push(geo);
}

/** Merges one object's parts. `null` when it had none — an empty station. */
function mergeParts(parts: Parts): THREE.BufferGeometry | null {
  if (parts.length === 0) return null;
  const merged = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  if (merged === null) throw new Error("hideoutProps: geometry merge failed");
  return merged;
}

function triangleCount(geo: THREE.BufferGeometry): number {
  const index = geo.getIndex();
  if (index !== null) return index.count / 3;
  return (geo.attributes["position"]?.count ?? 0) / 3;
}

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

export function buildHideoutProps(
  layout: HideoutLayout,
  opts: HideoutPropsOptions,
): BuiltHideoutProps {
  const group = new THREE.Group();
  group.name = "hideoutProps";

  // ONE material for every opaque merged mesh: they differ only in vertex
  // colour.
  const material = new THREE.MeshLambertMaterial({ vertexColors: true, dithering: true });
  const geometries: THREE.BufferGeometry[] = [];
  const lights: THREE.PointLight[] = [];
  let triangles = 0;

  const addMesh = (geo: THREE.BufferGeometry | null, name: string): void => {
    if (geo === null) return;
    geo.computeBoundingSphere();
    const mesh = new THREE.Mesh(geo, material);
    mesh.name = name;
    group.add(mesh);
    geometries.push(geo);
    triangles += triangleCount(geo);
  };

  // --- stations: authored in LOCAL space with the front at −Z, then spun and
  // dropped onto the slot.
  for (const slot of layout.stations) {
    const level = layout.levels[slot.moduleId] ?? 0;
    const parts: Parts = [];
    if (level <= 0) buildJunkPile(parts, slot.moduleId);
    else buildStation(parts, slot.moduleId, level, opts.generatorRunning);
    const geo = mergeParts(parts);
    if (geo === null) continue;
    if (slot.yaw !== 0) geo.rotateY(slot.yaw);
    geo.translate(slot.pos.x, slot.pos.y, slot.pos.z);
    addMesh(geo, `station:${slot.moduleId}`);
  }

  // --- the paint on the target faces, in WORLD space -----------------------
  if (layout.targets.length > 0) addMesh(buildTargetDressing(layout), "targets");

  // --- the mess ------------------------------------------------------------
  addMesh(buildClutter(layout), "clutter");
  addMesh(buildStructure(), "structure");

  // --- lights --------------------------------------------------------------
  const bulbs: Parts = [];
  for (const spec of lampSpecs(layout, opts.generatorRunning)) {
    const light = new THREE.PointLight(spec.color, spec.intensity, spec.distance, LAMP_DECAY);
    light.position.set(spec.x, spec.y, spec.z);
    group.add(light);
    lights.push(light);
    // The bulb itself, painted not emissive — the point light inside it is
    // enough.
    addBox(bulbs, 0.16, 0.1, 0.16, spec.x, spec.y + 0.06, spec.z, spec.color);
    addBox(bulbs, 0.05, 0.18, 0.05, spec.x, spec.y + 0.24, spec.z, C_IRON);
  }
  const bulbGeo = mergeParts(bulbs);
  const bulbMaterial = new THREE.MeshBasicMaterial({ vertexColors: true });
  if (bulbGeo !== null) {
    bulbGeo.computeBoundingSphere();
    const mesh = new THREE.Mesh(bulbGeo, bulbMaterial);
    mesh.name = "bulbs";
    group.add(mesh);
    geometries.push(bulbGeo);
    triangles += triangleCount(bulbGeo);
  }

  // --- target glows --------------------------------------------------------
  const glowMaterial = new THREE.MeshBasicMaterial({
    color: GLOW_COLOR,
    transparent: true,
    opacity: GLOW_OPACITY,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const targetGlows = new Map<string, THREE.Object3D>();
  for (const target of layout.targets) {
    const b = target.box;
    const geo = new THREE.BoxGeometry(
      (b.max.x - b.min.x) * GLOW_SCALE,
      (b.max.y - b.min.y) * GLOW_SCALE,
      (b.max.z - b.min.z) * GLOW_SCALE,
    );
    const mesh = new THREE.Mesh(geo, glowMaterial);
    mesh.name = `glow:${target.id}`;
    mesh.position.set(
      (b.min.x + b.max.x) * 0.5,
      (b.min.y + b.max.y) * 0.5,
      (b.min.z + b.max.z) * 0.5,
    );
    // Off until a drill lights it. The shells exist at all times so that
    // lighting one is a boolean rather than an allocation mid-drill.
    mesh.visible = false;
    group.add(mesh);
    geometries.push(geo);
    targetGlows.set(target.id, mesh);
  }

  let disposed = false;
  return {
    group,
    triangles,
    targetGlows,
    dispose(): void {
      // Idempotent: the session disposes on a rebuild and `WorldView` disposes
      // again with the map root, and neither knows about the other.
      if (disposed) return;
      disposed = true;
      for (const geo of geometries) geo.dispose();
      for (const light of lights) light.dispose();
      material.dispose();
      bulbMaterial.dispose();
      glowMaterial.dispose();
      group.clear();
    },
  };
}

// ---------------------------------------------------------------------------
// Lights
// ---------------------------------------------------------------------------

interface LampSpec {
  x: number;
  y: number;
  z: number;
  color: number;
  intensity: number;
  distance: number;
}

/**
 * Where the light comes from. Two states, no dimmer: with fuel the shelter is a
 * workshop, without it a cellar with three emergency lamps — still navigable,
 * so a player can find the generator.
 */
function lampSpecs(layout: HideoutLayout, running: boolean): LampSpec[] {
  const out: LampSpec[] = [];
  const warm = (x: number, z: number, y = LAMP_Y): void => {
    out.push({
      x,
      y,
      z,
      color: LAMP_WARM,
      intensity: LAMP_WARM_INTENSITY,
      distance: LAMP_WARM_DISTANCE,
    });
  };
  const cold = (x: number, z: number, y = LAMP_Y): void => {
    out.push({
      x,
      y,
      z,
      color: LAMP_COLD,
      intensity: LAMP_COLD_INTENSITY,
      distance: LAMP_COLD_DISTANCE,
    });
  };

  if (!running) {
    cold(-7, 0);
    cold(7, 0);
    cold(0, HALL_MAX_Z - 0.4, 3.0);
    cold(11, -11.5);
    return out;
  }

  // A grid over the hall: eight bulbs is what 28 × 18 m needs before the
  // corners stop being holes.
  for (const x of [-10.5, -3.5, 3.5, 10.5]) {
    for (const z of [-4.5, 4.5]) warm(x, z);
  }
  // The alcove is a hole in the wall; without its own bulb the generator sits
  // in a shadow the hall's lights cannot reach into.
  warm(11, -11.5);
  // The tunnel is only lit once it is a range — before that it is behind a
  // metre of rubble and there is nothing in it to see.
  if ((layout.levels["schiessstand"] ?? 0) >= 1) {
    for (const x of TUNNEL_LAMP_X) warm(x, 0, TUNNEL_LAMP_Y);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Structure
// ---------------------------------------------------------------------------

/**
 * Four I-beam stand-ins under the slab, over the pillar rows — the grain that
 * keeps a 28 m ceiling from reading as one grey plane.
 */
function buildStructure(): THREE.BufferGeometry | null {
  const parts: Parts = [];
  const y = CEIL_Y - 0.15;
  const depth = HALL_MAX_Z - HALL_MIN_Z;
  const width = HALL_MAX_X - HALL_MIN_X;
  for (const x of [-7, 7]) addBox(parts, 0.3, 0.3, depth, x, y, 0, C_IRON);
  for (const z of [-3, 3]) addBox(parts, width, 0.3, 0.3, 0, y, z, C_IRON);
  return mergeParts(parts);
}

// ---------------------------------------------------------------------------
// Unbuilt stations
// ---------------------------------------------------------------------------

/**
 * What sits on a slot before the module exists: a tarpaulin over crates and
 * rubble. One shape for all nine, varied by a hash of the module id.
 */
function buildJunkPile(parts: Parts, moduleId: string): void {
  const seed = seedOf(moduleId);
  // The tarp: wide, low and the only saturated thing in the pile, so the eye
  // finds it across a dark hall.
  addBox(parts, 1.7, 0.55, 1.1, 0, 0.28, 0, C_TARP, randSym(seed, 1) * 0.4);
  addBox(parts, 1.5, 0.12, 0.95, 0, 0.6, 0, C_TARP, randSym(seed, 2) * 0.4);

  const n = 3 + Math.floor(rand01(seed, 3) * 3);
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + rand01(seed, 10 + i);
    const r = 0.75 + rand01(seed, 20 + i) * 0.5;
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r * 0.7;
    const s = 0.28 + rand01(seed, 30 + i) * 0.24;
    const crate = rand01(seed, 40 + i) > 0.45;
    addBox(
      parts,
      s,
      s * (crate ? 0.9 : 0.6),
      s,
      x,
      (s * (crate ? 0.9 : 0.6)) / 2,
      z,
      crate ? C_WOOD_DARK : C_RUBBLE,
      randSym(seed, 50 + i) * 1.2,
    );
  }
  // One thing sticking out of it, so the pile has a silhouette above knee
  // height instead of being a lump on the floor.
  addTilted(parts, 0.08, 1.5, 0.08, 0.5, 0.6, -0.3, C_RUST, 0.5, randSym(seed, 4), 0.3);
}

// ---------------------------------------------------------------------------
// Built stations
// ---------------------------------------------------------------------------

/** Dispatch. Local space, front at −Z, floor at y = 0. */
function buildStation(
  parts: Parts,
  moduleId: string,
  level: number,
  generatorRunning: boolean,
): void {
  switch (moduleId) {
    case "lager":
      return buildLager(parts, level);
    case "generator":
      return buildGenerator(parts, level, generatorRunning);
    case "werkbank":
      return buildWerkbank(parts, level);
    case "sanitaet":
      return buildSanitaet(parts, level);
    case "brennerei":
      return buildBrennerei(parts, level);
    case "bienenstock":
      return buildBienenstock(parts, level);
    case "schiessstand":
      return buildSchiessstand(parts, level);
    case "kraftraum":
      return buildKraftraum(parts, level);
    case "funkraum":
      return buildFunkraum(parts, level);
    default:
      // A module this file has never heard of still gets a slot — see the note
      // on `HIDEOUT_MODULE_ORDER`. A crate is a better answer than nothing.
      addBox(parts, 0.9, 0.9, 0.6, 0, 0.45, 0, C_WOOD);
      return;
  }
}

/** One shelving unit: two uprights and three boards. */
function shelfUnit(parts: Parts, cx: number, seed: number): void {
  const w = 1.6;
  for (const side of [-1, 1]) {
    addBox(parts, 0.07, 1.9, 0.07, cx + (side * w) / 2, 0.95, -0.2, C_STEEL_DARK);
    addBox(parts, 0.07, 1.9, 0.07, cx + (side * w) / 2, 0.95, 0.2, C_STEEL_DARK);
  }
  for (let i = 0; i < 3; i++) {
    const y = 0.5 + i * 0.62;
    addBox(parts, w, 0.05, 0.5, cx, y, 0, C_WOOD);
    // Whatever is on that board.
    const n = 2 + Math.floor(rand01(seed, i) * 2);
    for (let j = 0; j < n; j++) {
      const x = cx - w / 2 + 0.25 + (j * (w - 0.5)) / Math.max(1, n - 1);
      const h = 0.18 + rand01(seed, i * 8 + j) * 0.16;
      addBox(
        parts,
        0.24,
        h,
        0.3,
        x,
        y + 0.025 + h / 2,
        randSym(seed, i * 16 + j) * 0.05,
        j % 2 === 0 ? C_WOOD_DARK : C_STEEL,
        randSym(seed, i * 32 + j) * 0.25,
      );
    }
  }
}

function buildLager(parts: Parts, level: number): void {
  shelfUnit(parts, -0.9, 71);
  if (level >= 2) shelfUnit(parts, 0.9, 73);
  // A stack of sacks on the floor: the shelves alone leave a metre of empty
  // wall under them, which reads as a shop display rather than as storage.
  addBox(parts, 0.7, 0.3, 0.45, level >= 2 ? -1.9 : 0.6, 0.15, 0.1, C_CANVAS, 0.2);
  addBox(parts, 0.6, 0.26, 0.4, level >= 2 ? -1.9 : 0.6, 0.43, 0.1, C_CANVAS, -0.35);
}

function buildGenerator(parts: Parts, level: number, running: boolean): void {
  // Skid, engine block, exhaust up into the ceiling.
  addBox(parts, 1.5, 0.16, 0.9, 0, 0.08, 0, C_IRON);
  addBox(parts, 1.1, 0.62, 0.7, -0.1, 0.47, 0, C_OXIDE);
  addBox(parts, 0.5, 0.34, 0.5, 0.45, 0.33, 0, C_STEEL_DARK);
  addCyl(parts, 0.09, 0.09, 3.0, 0.45, 0.5, -0.28, C_IRON, 8);
  // Radiator grille, for a face.
  for (let i = 0; i < 4; i++) {
    addBox(parts, 0.06, 0.44, 0.02, -0.55 + i * 0.06, 0.5, -0.36, C_IRON);
  }
  // Jerrycan.
  addBox(parts, 0.28, 0.42, 0.16, -0.95, 0.21, -0.3, C_TARP, 0.3);
  if (level >= 2) {
    // A second tank, plumbed in — the level that halves the burn.
    addCyl(parts, 0.32, 0.32, 0.9, 1.15, 0, 0.1, C_STEEL_DARK, 12);
    addBox(parts, 0.06, 0.06, 0.7, 0.75, 0.75, 0.05, C_IRON);
  }
  if (running) {
    // The one thing in the shelter that says "there is power": a lamp the
    // Lambert shading cannot dim, sitting on the control panel.
    addBox(parts, 0.09, 0.09, 0.05, 0.45, 0.56, -0.28, C_LAMP_ON);
  }
}

function buildWerkbank(parts: Parts, level: number): void {
  // Heavy bench: top, four legs, a rail.
  addBox(parts, 2.0, 0.09, 0.75, 0, 0.86, 0, C_WOOD);
  for (const sx of [-0.88, 0.88]) {
    for (const sz of [-0.3, 0.3]) addBox(parts, 0.09, 0.82, 0.09, sx, 0.41, sz, C_WOOD_DARK);
  }
  addBox(parts, 1.9, 0.07, 0.06, 0, 0.25, 0.28, C_WOOD_DARK);
  // Vice, on the corner where a vice lives.
  addBox(parts, 0.2, 0.16, 0.26, -0.78, 0.98, -0.14, C_STEEL_DARK);
  addBox(parts, 0.07, 0.07, 0.34, -0.78, 1.02, -0.14, C_STEEL);
  // Lamp arm.
  addBox(parts, 0.05, 0.5, 0.05, 0.72, 1.15, 0.2, C_IRON);
  addTilted(parts, 0.05, 0.4, 0.05, 0.6, 1.42, 0.1, C_IRON, 0, 0, 0.7);
  addBox(parts, 0.18, 0.1, 0.18, 0.44, 1.55, 0.1, C_LAMP_ON);
  // Cartridge boxes.
  for (let i = 0; i < 3; i++) {
    addBox(parts, 0.2, 0.1, 0.14, -0.1 + i * 0.24, 0.96, 0.12, i === 1 ? C_RED : C_PAPER, 0.1 * i);
  }
  // Pegboard on the wall behind, with tools hanging off it.
  addBox(parts, 1.6, 1.0, 0.05, 0, 1.75, 0.36, C_WOOD_DARK);
  for (let i = 0; i < 5; i++) {
    addTilted(
      parts,
      0.05,
      0.34,
      0.04,
      -0.6 + i * 0.3,
      1.72,
      0.31,
      C_STEEL,
      0,
      0,
      randSym(81, i) * 0.4,
    );
  }
  if (level >= 2) {
    // The press: the tall narrow silhouette that says the bench got serious.
    addBox(parts, 0.34, 1.5, 0.34, 1.35, 0.75, 0, C_STEEL_DARK);
    addBox(parts, 0.5, 0.12, 0.5, 1.35, 1.56, 0, C_IRON);
    addBox(parts, 0.08, 0.4, 0.08, 1.35, 1.8, 0, C_STEEL);
    addBox(parts, 0.06, 0.06, 0.44, 1.35, 1.96, 0.18, C_RED);
  }
}

function buildSanitaet(parts: Parts, level: number): void {
  // Folding table under a white cloth.
  addBox(parts, 1.5, 0.06, 0.7, 0, 0.78, 0, C_WHITE);
  addBox(parts, 1.56, 0.14, 0.76, 0, 0.72, 0, C_WHITE);
  for (const sx of [-0.62, 0.62]) {
    addTilted(parts, 0.06, 0.75, 0.06, sx, 0.37, -0.22, C_STEEL_DARK, 0.12, 0, 0);
    addTilted(parts, 0.06, 0.75, 0.06, sx, 0.37, 0.22, C_STEEL_DARK, -0.12, 0, 0);
  }
  // The red-cross box, and a red cross on it.
  addBox(parts, 0.4, 0.26, 0.3, -0.4, 0.94, 0, C_WHITE);
  addBox(parts, 0.16, 0.05, 0.05, -0.4, 1.075, -0.14, C_RED);
  addBox(parts, 0.05, 0.05, 0.16, -0.4, 1.075, -0.14, C_RED);
  // Bottles and a lamp.
  for (let i = 0; i < 3; i++) {
    addCyl(parts, 0.05, 0.06, 0.24, 0.15 + i * 0.16, 0.81, 0.1, C_GLASS, 8);
  }
  addBox(parts, 0.12, 0.3, 0.12, 0.6, 0.81, -0.2, C_IRON);
  addBox(parts, 0.2, 0.12, 0.2, 0.6, 1.15, -0.2, C_LAMP_ON);
  // Stool.
  addBox(parts, 0.34, 0.05, 0.34, 0.1, 0.48, -0.75, C_WOOD);
  for (const sx of [-0.13, 0.13]) {
    for (const sz of [-0.13, 0.13]) {
      addBox(parts, 0.05, 0.46, 0.05, 0.1 + sx, 0.23, -0.75 + sz, C_WOOD_DARK);
    }
  }
  if (level >= 2) {
    // A cabinet with glass in the doors — the level that makes this a clinic.
    addBox(parts, 0.8, 1.7, 0.4, 1.35, 0.85, 0.15, C_WHITE);
    addBox(parts, 0.68, 0.6, 0.03, 1.35, 1.35, -0.06, C_GLASS);
    addBox(parts, 0.03, 0.6, 0.05, 1.35, 1.35, -0.07, C_STEEL_DARK);
  }
}

function buildBrennerei(parts: Parts, level: number): void {
  // The still: a copper pot on a gas ring, with the swan neck and the coil.
  addCyl(parts, 0.34, 0.4, 0.75, 0, 0.28, 0, C_COPPER, 12);
  addCyl(parts, 0.14, 0.32, 0.35, 0, 1.03, 0, C_COPPER, 12);
  addCyl(parts, 0.1, 0.1, 0.3, 0, 1.38, 0, C_COPPER, 8);
  addTilted(parts, 0.09, 0.55, 0.09, 0.22, 1.66, 0, C_COPPER, 0, 0, -0.9);
  // The worm: five rings of thin box stepping down into the tub.
  for (let i = 0; i < 5; i++) {
    addTilted(parts, 0.5, 0.05, 0.05, 0.52, 1.5 - i * 0.16, 0, C_COPPER, 0, i * 0.7, 0.1);
  }
  addCyl(parts, 0.26, 0.26, 0.55, 0.6, 0, 0, C_WOOD_DARK, 10);
  // The gas ring under it, and the legs holding the pot off it.
  addCyl(parts, 0.22, 0.22, 0.12, 0, 0.12, 0, C_IRON, 8);
  for (const a of [0, 2.1, 4.2]) {
    addBox(parts, 0.05, 0.28, 0.05, Math.cos(a) * 0.3, 0.14, Math.sin(a) * 0.3, C_IRON);
  }
  // Bottles, because a still with nothing coming out of it is a kettle.
  for (let i = 0; i < 4; i++) {
    addCyl(parts, 0.045, 0.055, 0.26, -0.55 + (i % 2) * 0.14, 0, -0.45 + Math.floor(i / 2) * 0.16, C_GLASS, 8);
  }
  addBox(parts, 0.5, 0.35, 0.4, -0.75, 0.17, 0.3, C_WOOD, 0.4);
  if (level >= 2) {
    // The still has one level in today's content; the mash tub is here so that
    // a second one has somewhere to land rather than needing this file reopened.
    addCyl(parts, 0.3, 0.3, 0.7, -0.9, 0, -0.4, C_WOOD_DARK, 10);
  }
}

function buildBienenstock(parts: Parts, level: number): void {
  // A stand, so the hives are not standing in the damp.
  addBox(parts, 1.3, 0.12, 0.6, 0, 0.2, 0, C_WOOD_DARK);
  for (const sx of [-0.5, 0.5]) addBox(parts, 0.1, 0.26, 0.5, sx, 0.13, 0, C_WOOD_DARK);
  const stack = level >= 2 ? 3 : 2;
  for (let i = 0; i < stack; i++) {
    addBox(parts, 0.6, 0.32, 0.5, -0.3, 0.42 + i * 0.34, 0, C_HIVE);
    addBox(parts, 0.64, 0.04, 0.54, -0.3, 0.6 + i * 0.34, 0, C_WOOD);
  }
  addBox(parts, 0.6, 0.3, 0.5, 0.36, 0.42, 0, C_HIVE);
  addBox(parts, 0.68, 0.05, 0.58, 0.36, 0.6, 0, C_WOOD_DARK);
  // The vent grille the niche exists for.
  addBox(parts, 0.9, 0.6, 0.06, 0, 2.2, 0.42, C_IRON);
  for (let i = 0; i < 5; i++) addBox(parts, 0.84, 0.05, 0.1, 0, 1.98 + i * 0.11, 0.4, C_STEEL_DARK);
}

/** Paint on the lane markings: hazard yellow, the one saturated thing down here. */
const C_LANE = 0xd6b345;

function buildSchiessstand(parts: Parts, level: number): void {
  // Target faces, posts and the table are SOLIDS in `layout.ts`; this is the
  // furniture around them, in the station's local frame — yaw −π/2, so local
  // −Z runs downrange and local +X is world +Z.
  const wallX = TUNNEL_HALF_W - 0.35;
  for (let row = 0; row < 11; row++) {
    const z = -1.6 - row * 0.7;
    for (let tier = 0; tier < 3 - (row % 2); tier++) {
      for (const side of [-1, 1]) {
        addBox(
          parts,
          0.34,
          0.24,
          0.62,
          side * (wallX + randSym(90, row * 2 + tier) * 0.04),
          0.12 + tier * 0.24,
          z,
          C_CANVAS,
          randSym(91, row * 4 + tier) * 0.22,
        );
      }
    }
  }
  // On the table top: the ammunition crate and two boxes off it.
  addBox(parts, 0.5, 0.3, 0.36, 2.7, 1.05, 0, C_WOOD_DARK);
  addBox(parts, 0.44, 0.05, 0.3, 2.7, 1.23, 0, C_STEEL_DARK);
  addBox(parts, 0.18, 0.09, 0.12, 2.7, 0.945, 0.42, C_PAPER, 0.2);
  addBox(parts, 0.18, 0.09, 0.12, 2.62, 0.945, -0.38, C_RED, -0.3);
  // The firing line, and the two lane lines running away from it.
  addBox(parts, 5.8, 0.01, 0.08, 0, 0.005, -1.2, C_LANE);
  for (const sx of [-1.0, 1.0]) addBox(parts, 0.05, 0.01, 34.7, sx, 0.005, -18.65, C_LANE);
  // Distance boards on both walls, at eye height.
  for (const d of [10, 20, 30]) {
    for (const side of [-1, 1]) {
      addBox(parts, 0.03, 0.25, 0.25, side * (TUNNEL_HALF_W - 0.03), 1.7, -d, C_WHITE);
    }
  }
  // Shades for the tunnel bulbs — see `lampSpecs` for the real lights.
  for (const x of TUNNEL_LAMP_X) {
    addBox(parts, 0.3, 0.1, 0.3, 0, TUNNEL_H - 0.15, -(x - FIRING_X), C_IRON);
  }
  if (level >= 2) {
    // The drill timer: a box on the wall with a red lamp that the range
    // controller's glows answer to.
    addBox(parts, 0.3, 0.4, 0.2, 2.85, 1.5, -0.6, C_STEEL_DARK);
    addBox(parts, 0.05, 0.1, 0.1, 2.67, 1.62, -0.6, C_RED);
  }
}

/**
 * The paint on each target face, in WORLD space: an aiming square on a plate,
 * a torso and head on a silhouette, a hanging frame around a gong. Downrange
 * is +x, so the shooter's side of a face is `box.min.x`.
 */
function buildTargetDressing(layout: HideoutLayout): THREE.BufferGeometry | null {
  const parts: Parts = [];
  for (const target of layout.targets) {
    const b = target.box;
    const front = b.min.x - 0.006;
    const xc = (b.min.x + b.max.x) / 2;
    const yc = (b.min.y + b.max.y) / 2;
    const zc = (b.min.z + b.max.z) / 2;
    switch (target.kind) {
      case "plate":
        addBox(parts, 0.005, 0.3, 0.3, front, yc, zc, C_STEEL_DARK);
        break;
      case "silhouette":
        addBox(parts, 0.01, 0.62, 0.42, front, b.min.y + 0.45, zc, C_SILHOUETTE);
        addBox(parts, 0.01, 0.24, 0.22, front, b.min.y + 0.95, zc, C_SILHOUETTE);
        for (const z of [b.min.z + 0.025, b.max.z - 0.025]) {
          addBox(parts, 0.05, 1.25, 0.05, b.max.x + 0.03, yc, z, C_WOOD_DARK);
        }
        break;
      case "gong": {
        const postH = b.max.y + 0.35;
        for (const z of [zc - 0.75, zc + 0.75]) {
          addBox(parts, 0.1, postH, 0.1, xc, postH / 2, z, C_IRON);
        }
        addBox(parts, 0.1, 0.1, 1.6, xc, b.max.y + 0.3, zc, C_IRON);
        for (const z of [zc - 0.38, zc + 0.38]) {
          addBox(parts, 0.03, 0.3, 0.03, xc, b.max.y + 0.15, z, C_IRON);
        }
        addBox(parts, 0.005, 0.5, 0.5, front, yc, zc, C_STEEL_DARK);
        break;
      }
    }
  }
  return mergeParts(parts);
}

function buildKraftraum(parts: Parts, level: number): void {
  // Rubber mat.
  addBox(parts, 2.4, 0.04, 1.6, 0, 0.02, 0, C_RUBBER);
  // Bench press: bench, uprights, bar with plates.
  addBox(parts, 1.5, 0.14, 0.34, -0.3, 0.48, 0, C_RUBBER);
  for (const sx of [-0.9, 0.3]) addBox(parts, 0.1, 0.42, 0.3, sx, 0.21, 0, C_IRON);
  for (const sz of [-0.5, 0.5]) {
    addBox(parts, 0.09, 1.15, 0.09, -0.75, 0.57, sz, C_IRON);
  }
  addBox(parts, 0.06, 0.06, 1.5, -0.75, 1.18, 0, C_STEEL);
  for (const sz of [-0.62, 0.62]) {
    addCyl(parts, 0.22, 0.22, 0.06, -0.75, 1.15, sz, C_IRON, 12);
  }
  // Dumbbell rack.
  addBox(parts, 1.2, 0.08, 0.4, 1.3, 0.55, 0.3, C_IRON);
  addBox(parts, 1.2, 0.08, 0.4, 1.3, 0.2, 0.3, C_IRON);
  for (let i = 0; i < 4; i++) {
    const r = 0.09 + i * 0.012;
    addCyl(parts, r, r, 0.1, 0.85 + i * 0.28, 0.59, 0.3, C_STEEL_DARK, 10);
    addCyl(parts, r, r, 0.1, 0.85 + i * 0.28, 0.24, 0.3, C_STEEL_DARK, 10);
  }
  // Punch bag, hanging.
  addCyl(parts, 0.19, 0.17, 1.1, 0.6, 1.15, -1.1, C_WOOD_DARK, 10);
  addBox(parts, 0.05, 0.95, 0.05, 0.6, 2.3, -1.1, C_IRON);
  if (level >= 2) {
    // Pull-up bar across the corner, and the plates that came with it.
    for (const sx of [-1.5, 1.5]) addBox(parts, 0.08, 2.1, 0.08, sx, 1.05, -1.6, C_IRON);
    addBox(parts, 3.0, 0.07, 0.07, 0, 2.06, -1.6, C_STEEL);
    for (let i = 0; i < 3; i++) {
      addTilted(parts, 0.44, 0.44, 0.07, -1.7, 0.24, 0.4 + i * 0.12, C_IRON, 0, 0, Math.PI / 2);
    }
  }
}

function buildFunkraum(parts: Parts, level: number): void {
  // Desk.
  addBox(parts, 1.7, 0.07, 0.7, 0, 0.76, 0, C_WOOD);
  for (const sx of [-0.75, 0.75]) addBox(parts, 0.1, 0.75, 0.6, sx, 0.38, 0, C_WOOD_DARK);
  // The set: two stacked boxes, a dial, a speaker grille.
  addBox(parts, 0.62, 0.34, 0.4, -0.3, 0.97, 0.05, C_STEEL_DARK);
  addBox(parts, 0.5, 0.24, 0.34, -0.3, 1.26, 0.05, C_IRON);
  addCyl(parts, 0.07, 0.07, 0.04, -0.5, 1.36, -0.13, C_STEEL, 10);
  addBox(parts, 0.16, 0.16, 0.03, -0.1, 1.28, -0.16, C_RUBBER);
  addBox(parts, 0.05, 0.05, 0.03, -0.1, 1.06, -0.19, level >= 2 ? C_RED : C_LAMP_COLD);
  // Antenna wire climbing the wall behind.
  addBox(parts, 0.03, 2.2, 0.03, 0.55, 1.9, 0.32, C_COPPER);
  addTilted(parts, 0.03, 0.9, 0.03, 0.3, 1.3, 0.3, C_COPPER, 0, 0, 0.6);
  // Kettle, and a mug.
  addCyl(parts, 0.09, 0.11, 0.2, 0.6, 0.8, -0.1, C_STEEL, 10);
  addCyl(parts, 0.05, 0.05, 0.09, 0.4, 0.8, -0.22, C_WHITE, 8);
  // Chair.
  addBox(parts, 0.42, 0.05, 0.4, 0, 0.45, -0.75, C_WOOD);
  addBox(parts, 0.42, 0.5, 0.05, 0, 0.7, -0.93, C_WOOD);
  for (const sx of [-0.16, 0.16]) {
    for (const sz of [-0.16, 0.16]) {
      addBox(parts, 0.05, 0.44, 0.05, sx, 0.22, -0.75 + sz, C_WOOD_DARK);
    }
  }
  // The wall of maps: paper, pins and the red string between them.
  for (let i = 0; i < 4; i++) {
    addTilted(
      parts,
      0.5 + rand01(60, i) * 0.2,
      0.42,
      0.02,
      -0.9 + i * 0.6,
      1.95 + randSym(61, i) * 0.12,
      0.33,
      C_PAPER,
      0,
      0,
      randSym(62, i) * 0.1,
    );
  }
  for (let i = 0; i < 3; i++) {
    addTilted(parts, 0.75, 0.015, 0.015, -0.6 + i * 0.6, 1.95, 0.31, C_RED, 0, 0, i % 2 === 0 ? 0.5 : -0.5);
  }
  if (level >= 2) {
    // Second desk, a lamp, a corkboard.
    addBox(parts, 1.1, 0.07, 0.6, 1.55, 0.76, -0.5, C_WOOD, 0.5);
    addBox(parts, 0.09, 0.75, 0.5, 1.9, 0.38, -0.35, C_WOOD_DARK);
    addBox(parts, 0.09, 0.4, 0.09, 1.4, 0.99, -0.6, C_IRON);
    addBox(parts, 0.2, 0.12, 0.2, 1.4, 1.24, -0.6, C_LAMP_ON);
    addBox(parts, 0.9, 0.6, 0.04, 1.85, 1.8, 0.3, C_WOOD_DARK);
    for (let i = 0; i < 4; i++) {
      addBox(parts, 0.18, 0.22, 0.01, 1.55 + (i % 2) * 0.4, 1.68 + Math.floor(i / 2) * 0.28, 0.27, C_PAPER);
    }
  }
}

// ---------------------------------------------------------------------------
// Clutter
// ---------------------------------------------------------------------------

type ClutterKind = "rubble" | "drum" | "chair" | "table" | "shelf" | "planks" | "puddle" | "cable";

interface ClutterItem {
  kind: ClutterKind;
  x: number;
  z: number;
  /** Fixed, so the same piece of junk is the same shape on every build. */
  seed: number;
  /** Gone once `builtCount` reaches this. 10 means never. */
  clearedAt: number;
}

/**
 * Where the mess is. Authored by hand so nothing sits in a station's footprint,
 * against a pillar, on the door step or in the tunnel; `buildClutter` re-checks
 * the station rule at build time. Two items never clear, so the finished
 * shelter still reads as lived in.
 */
const CLUTTER: readonly ClutterItem[] = [
  { kind: "rubble", x: -5.0, z: 6.5, seed: 101, clearedAt: 1 },
  { kind: "rubble", x: 2.0, z: -4.0, seed: 102, clearedAt: 2 },
  { kind: "rubble", x: 9.0, z: 3.5, seed: 103, clearedAt: 3 },
  { kind: "rubble", x: -12.0, z: 0.5, seed: 104, clearedAt: 4 },
  { kind: "planks", x: -3.0, z: -7.0, seed: 105, clearedAt: 2 },
  { kind: "planks", x: 5.5, z: -6.0, seed: 106, clearedAt: 5 },
  { kind: "chair", x: -9.5, z: 2.0, seed: 107, clearedAt: 3 },
  { kind: "chair", x: 4.0, z: 6.0, seed: 108, clearedAt: 6 },
  { kind: "table", x: -4.5, z: -1.5, seed: 109, clearedAt: 5 },
  { kind: "shelf", x: 8.5, z: -6.5, seed: 110, clearedAt: 7 },
  { kind: "drum", x: -12.5, z: -8.5, seed: 111, clearedAt: 6 },
  { kind: "drum", x: -2.5, z: 8.0, seed: 112, clearedAt: 9 },
  { kind: "drum", x: 8.9, z: -9.9, seed: 113, clearedAt: 10 },
  { kind: "puddle", x: -6.0, z: -0.5, seed: 114, clearedAt: 6 },
  { kind: "cable", x: -8.0, z: 4.5, seed: 115, clearedAt: 8 },
  { kind: "cable", x: 3.0, z: -2.0, seed: 116, clearedAt: 10 },
];

function buildClutter(layout: HideoutLayout): THREE.BufferGeometry | null {
  const parts: Parts = [];
  for (const item of CLUTTER) {
    if (layout.builtCount >= item.clearedAt) continue;
    if (inAnyStation(layout, item.x, item.z)) continue;
    clutterGeometry(parts, item);
  }
  return mergeParts(parts);
}

/** The safety net behind the hand-placed list: never bury a station. */
function inAnyStation(layout: HideoutLayout, x: number, z: number): boolean {
  for (const s of layout.stations) {
    const b = s.interactBox;
    if (x >= b.min.x && x <= b.max.x && z >= b.min.z && z <= b.max.z) return true;
  }
  return false;
}

function clutterGeometry(parts: Parts, item: ClutterItem): void {
  const { x, z, seed } = item;
  switch (item.kind) {
    case "rubble": {
      const n = 4 + Math.floor(rand01(seed, 1) * 5);
      for (let i = 0; i < n; i++) {
        const s = 0.16 + rand01(seed, 10 + i) * 0.3;
        addTilted(
          parts,
          s,
          s * 0.7,
          s * 0.9,
          x + randSym(seed, 20 + i) * 0.6,
          s * 0.35,
          z + randSym(seed, 30 + i) * 0.6,
          i % 3 === 0 ? C_RUBBLE_DARK : C_RUBBLE,
          randSym(seed, 40 + i) * 0.4,
          randSym(seed, 50 + i) * 3,
          randSym(seed, 60 + i) * 0.4,
        );
      }
      return;
    }
    case "drum": {
      // On its side often enough that a row of them is not a row of pillars.
      const tipped = rand01(seed, 2) > 0.55;
      if (tipped) {
        addTilted(parts, 0.6, 0.86, 0.6, x, 0.3, z, C_RUST, Math.PI / 2, randSym(seed, 3) * 2, 0);
      } else {
        addCyl(parts, 0.3, 0.3, 0.88, x, 0, z, rand01(seed, 4) > 0.5 ? C_RUST : C_TARP, 12);
        addCyl(parts, 0.32, 0.32, 0.05, x, 0.4, z, C_IRON, 12);
      }
      return;
    }
    case "chair": {
      const yaw = randSym(seed, 5) * 3;
      // Fallen over, because a chair standing neatly in an abandoned shelter
      // is the one piece of furniture that looks deliberate.
      addTilted(parts, 0.42, 0.05, 0.4, x, 0.22, z, C_WOOD, 1.4, yaw, 0);
      addTilted(parts, 0.42, 0.46, 0.05, x + 0.16, 0.24, z, C_WOOD, 1.4, yaw, 0);
      for (let i = 0; i < 4; i++) {
        addTilted(
          parts,
          0.05,
          0.42,
          0.05,
          x - 0.12 + (i % 2) * 0.1,
          0.06 + Math.floor(i / 2) * 0.34,
          z - 0.15 + Math.floor(i / 2) * 0.3,
          C_WOOD_DARK,
          0,
          yaw,
          1.5,
        );
      }
      return;
    }
    case "table": {
      const yaw = randSym(seed, 6) * 3;
      addBox(parts, 1.1, 0.06, 0.62, x, 0.66, z, C_WOOD, yaw);
      // One leg gone: the reason it is junk and not furniture.
      for (let i = 0; i < 3; i++) {
        const sx = i === 0 || i === 2 ? -0.45 : 0.45;
        const sz = i < 2 ? -0.24 : 0.24;
        addBox(parts, 0.07, 0.64, 0.07, x + sx, 0.32, z + sz, C_WOOD_DARK, yaw);
      }
      addTilted(parts, 0.07, 0.64, 0.07, x + 0.6, 0.06, z + 0.4, C_WOOD_DARK, 1.5, yaw, 0);
      return;
    }
    case "shelf": {
      // A shelving unit that came off the wall and took its contents with it.
      const yaw = randSym(seed, 7) * 3;
      addTilted(parts, 1.7, 0.06, 0.5, x, 0.36, z, C_WOOD, 0, yaw, 1.15);
      addTilted(parts, 1.7, 0.06, 0.5, x + 0.35, 0.5, z + 0.1, C_WOOD, 0, yaw, 1.15);
      addTilted(parts, 0.07, 1.8, 0.07, x - 0.2, 0.2, z - 0.3, C_STEEL_DARK, 0, yaw, 1.4);
      for (let i = 0; i < 4; i++) {
        const s = 0.2 + rand01(seed, 70 + i) * 0.12;
        addTilted(
          parts,
          s,
          s * 0.8,
          s,
          x + randSym(seed, 80 + i) * 0.9,
          s * 0.4,
          z + randSym(seed, 90 + i) * 0.7,
          C_WOOD_DARK,
          0,
          randSym(seed, 100 + i) * 3,
          randSym(seed, 110 + i) * 0.5,
        );
      }
      return;
    }
    case "planks": {
      const n = 3 + Math.floor(rand01(seed, 8) * 3);
      for (let i = 0; i < n; i++) {
        addTilted(
          parts,
          1.6 + rand01(seed, 120 + i) * 0.8,
          0.05,
          0.18,
          x + randSym(seed, 130 + i) * 0.4,
          0.03 + i * 0.055,
          z + randSym(seed, 140 + i) * 0.4,
          i % 2 === 0 ? C_WOOD : C_WOOD_DARK,
          0,
          randSym(seed, 150 + i) * 1.4,
          randSym(seed, 160 + i) * 0.06,
        );
      }
      return;
    }
    case "puddle": {
      // 1 cm of standing water: the cheapest "this place is damp" at this
      // fidelity.
      addBox(parts, 1.6 + rand01(seed, 9) * 0.8, 0.01, 1.1, x, 0.006, z, 0x24282c, randSym(seed, 11) * 1.5);
      addBox(parts, 0.8, 0.01, 0.7, x + randSym(seed, 12) * 0.7, 0.008, z + randSym(seed, 13) * 0.5, 0x2c3136, randSym(seed, 14) * 1.5);
      return;
    }
    case "cable": {
      // Conduit that came off the ceiling and is hanging in a slack loop.
      const yaw = randSym(seed, 15) * 3;
      addTilted(parts, 2.2, 0.05, 0.05, x, CEIL_Y - 0.35, z, C_IRON, 0, yaw, 0.22);
      addTilted(parts, 1.6, 0.04, 0.04, x + 0.4, CEIL_Y - 0.68, z + 0.2, C_RUBBER, 0, yaw + 0.4, -0.35);
      addTilted(parts, 1.2, 0.04, 0.04, x - 0.3, CEIL_Y - 0.9, z - 0.1, C_RUBBER, 0, yaw - 0.5, 0.5);
      return;
    }
  }
}

/** A junk pile's shape follows its module's NAME, via the same string hash
 *  every other per-name tint in the renderer uses. */
function seedOf(moduleId: string): number {
  return hashString(moduleId);
}
